// filepath: /Users/nawab/Developer/Project/gcbuying/frontend/src/pages/dashboard/Referral.tsx
import { Helmet } from "react-helmet";
import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  useGetReferralQuery,
  useGetReferredUsersQuery,
  useGetReferralRewardsQuery,
  ReferredUser,
  ReferralReward,
} from "../../features/referrals/referralSlice";

export const Referral = () => {
  const [activeTab, setActiveTab] = useState<"users" | "rewards">("users");

  // Get referral data
  const {
    data: referralData,
    isLoading: referralLoading,
    error: referralError,
    refetch: refetchReferral,
  } = useGetReferralQuery();

  // Get referred users
  const {
    data: referredUsers,
    isLoading: referredUsersLoading,
    error: referredUsersError,
    refetch: refetchReferredUsers,
  } = useGetReferredUsersQuery();

  // Get referral rewards
  const {
    data: referralRewards,
    isLoading: referralRewardsLoading,
    error: referralRewardsError,
    refetch: refetchRewards,
  } = useGetReferralRewardsQuery();

  // Base URL for referral link
  const baseReferralUrl = "https://gcbuying.com/register?referral_code=";
  const referralUrl = referralData?.referralId
    ? `${baseReferralUrl}${referralData.referralId}`
    : "";

  // Function to copy referral link to clipboard
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  // Function to share referral link
  const shareReferral = useCallback(async () => {
    if (!referralUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "GCBuying Referral",
          text: `Join GCBuying with my referral code: ${referralData?.referralId}`,
          url: referralUrl,
        });
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      copyToClipboard(referralUrl);
    }
  }, [referralUrl, referralData?.referralId, copyToClipboard]);

  // Function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="font-dashboard pt-8 pb-12 lg:px-4">
      {/* meta data --- */}
      <Helmet>
        <title>Referral Program</title>
        <meta name="description" content="Referral Program"></meta>
        <link rel="canonical" href="https://gcbuying.com/app/referral" />
      </Helmet>
      {/* meta data --- */}

      <div className="container-lg grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 min-h-screen">
          <div className="bg-black flex flex-col px-4 lg:px-10 py-6">
            <h1 className="text-white text-2xl font-medium mb-6">
              Referral Program
            </h1>

            {/* Referral Card */}
            {referralLoading ? (
              <div className="flex-1 flex justify-center items-center py-20">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-white/50">Loading referral data...</p>
                </div>
              </div>
            ) : referralError ? (
              <div className="flex-1 flex justify-center items-center py-20">
                <div className="flex flex-col items-center">
                  <p className="text-red-500 text-center">
                    Failed to load referral data
                  </p>
                  <button
                    className="mt-4 bg-primary py-2 px-6 rounded-lg text-black font-medium"
                    onClick={() => refetchReferral()}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Referral Code Card */}
                <div className="bg-gray-700 rounded-xl p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mr-4">
                      <svg
                        className="w-6 h-6 text-primary"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">
                        Your Referral Code
                      </p>
                      <div className="flex items-center">
                        <h2 className="text-primary text-2xl font-bold">
                          {referralData?.referralId || "N/A"}
                        </h2>
                        <button
                          className="ml-2 text-primary hover:text-primary/80"
                          onClick={() =>
                            copyToClipboard(referralData?.referralId || "")
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Referral Link */}
                  <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between mb-4">
                    <p className="text-white/70 truncate flex-1 mr-2">
                      {referralUrl || "Loading..."}
                    </p>
                    <button
                      className="bg-primary/10 text-primary px-3 py-1.5 rounded-lg flex items-center"
                      onClick={() => copyToClipboard(referralUrl)}
                    >
                      <span className="mr-1">Copy</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        ></path>
                      </svg>
                    </button>
                  </div>

                  {/* Share Button */}
                  <button
                    className="bg-primary/10 text-primary py-3 w-full rounded-lg flex items-center justify-center"
                    onClick={shareReferral}
                  >
                    <span className="mr-2 font-medium">Share Referral</span>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      ></path>
                    </svg>
                  </button>
                </div>

                {/* Referral Stats */}
                <div className="bg-gray-700 rounded-xl p-6 mb-6">
                  <h2 className="text-white text-xl font-medium mb-4">
                    Referral Statistics
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div className="bg-indigo-900/30 rounded-xl p-4">
                      <p className="text-white/70 text-sm">Total Earned</p>
                      <p className="text-primary text-2xl font-bold">
                        ₦{referralData?.earned || 0}
                      </p>
                    </div>

                    <div className="bg-green-900/30 rounded-xl p-4 flex flex-col justify-between">
                      <div>
                        <p className="text-white/70 text-sm">Current Balance</p>
                        <p className="text-green-500 text-2xl font-bold">
                          ₦{referralData?.balance || 0}
                        </p>
                      </div>
                      <Link
                        to="/app/withdraw?source=referral"
                        className="mt-4 bg-primary py-2 px-4 rounded-lg text-black font-medium text-center"
                      >
                        Withdraw
                      </Link>
                    </div>
                  </div>

                  <div className="bg-orange-900/30 rounded-xl p-4">
                    <p className="text-white/70 text-sm">Withdrawn</p>
                    <p className="text-orange-500 text-2xl font-bold">
                      ₦{referralData?.withdrawn || 0}
                    </p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex mx-auto md:mx-0 mb-6 bg-gray-700 rounded-lg p-1 max-w-xs">
                  <button
                    className={`flex-1 py-2 px-4 rounded-md ${
                      activeTab === "users"
                        ? "bg-primary text-black"
                        : "text-white/80"
                    }`}
                    onClick={() => setActiveTab("users")}
                  >
                    Referred Users
                  </button>
                  <button
                    className={`flex-1 py-2 px-4 rounded-md ${
                      activeTab === "rewards"
                        ? "bg-primary text-black"
                        : "text-white/80"
                    }`}
                    onClick={() => setActiveTab("rewards")}
                  >
                    Earnings
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === "users" && (
                  <div className="bg-gray-700 rounded-xl p-6">
                    <h2 className="text-white text-xl font-medium mb-4">
                      Users You've Referred
                    </h2>

                    {referredUsersLoading ? (
                      <div className="py-10 flex justify-center">
                        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : referredUsersError ? (
                      <div className="text-center py-10">
                        <p className="text-red-500 mb-3">
                          Error loading referred users
                        </p>
                        <button
                          className="bg-primary py-2 px-6 rounded-lg text-black font-medium"
                          onClick={() => refetchReferredUsers()}
                        >
                          Try Again
                        </button>
                      </div>
                    ) : !referredUsers || referredUsers.length === 0 ? (
                      <div className="text-center py-10">
                        <p className="text-white/50">No referred users yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {referredUsers.map((user: ReferredUser) => (
                          <div
                            key={user._id}
                            className="flex items-center p-4 bg-gray-800 rounded-lg"
                          >
                            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center mr-3">
                              <svg
                                className="w-5 h-5 text-white/70"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  clipRule="evenodd"
                                ></path>
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium">
                                {user.user.name}
                              </p>
                              <p className="text-white/50 text-sm">
                                {user.user.email}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-white/50 text-xs">
                                {formatDate(user.createdAt)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "rewards" && (
                  <div className="bg-gray-700 rounded-xl p-6">
                    <h2 className="text-white text-xl font-medium mb-4">
                      Referral Earnings
                    </h2>

                    {referralRewardsLoading ? (
                      <div className="py-10 flex justify-center">
                        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : referralRewardsError ? (
                      <div className="text-center py-10">
                        <p className="text-red-500 mb-3">
                          Error loading earnings
                        </p>
                        <button
                          className="bg-primary py-2 px-6 rounded-lg text-black font-medium"
                          onClick={() => refetchRewards()}
                        >
                          Try Again
                        </button>
                      </div>
                    ) : !referralRewards || referralRewards.length === 0 ? (
                      <div className="text-center py-10">
                        <p className="text-white/50">No earnings yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {referralRewards.map((reward: ReferralReward) => (
                          <div
                            key={reward._id}
                            className="flex items-center p-4 bg-gray-800 rounded-lg"
                          >
                            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center mr-3">
                              <svg
                                className="w-5 h-5 text-white/70"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  clipRule="evenodd"
                                ></path>
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium">
                                {reward.referralUser.name}
                              </p>
                              <p className="text-white/50 text-sm">
                                {reward.txType} • {formatDate(reward.createdAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-green-500 font-bold">
                                +₦{reward.amount}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right sidebar - information about the referral program */}
        <div className="bg-black h-[105vh] px-2 py-4 z-0">
          <div className="flex flex-col h-screen gap-4 max-w-[500px] mx-auto">
            <div className="bg-gray-700 p-6 rounded-lg">
              <h3 className="text-xl text-white mb-4">About Referrals</h3>
              <p className="text-white/70 mb-3">
                Share your referral code with friends and earn rewards when they
                trade on GCBuying.
              </p>
              <p className="text-white/70 mb-3">
                <span className="text-primary font-medium">How it works:</span>{" "}
                {referralData?.rewardAmount != null &&
                referralData?.minTradeValue != null
                  ? `Earn ₦${referralData.rewardAmount.toLocaleString()} each time a friend completes a trade worth ₦${referralData.minTradeValue.toLocaleString()} or more.`
                  : "Earn a bonus each time a friend completes a qualifying trade."}
              </p>

              <p className="text-white/70 mt-6 text-sm">
                Your referral rewards are automatically credited to your
                referral wallet balance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;
